import { Component, ErrorInfo, ReactNode } from 'react';
import { Result as AntResult, Button } from 'antd';
import { AlertCircle } from 'lucide-react';

// Type workaround for Result component
const Result = AntResult as any;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4"
          role="alert"
          aria-live="assertive"
        >
          <Result
            status="error"
            icon={<AlertCircle className="w-16 h-16 text-red-500" aria-hidden="true" />}
            title={
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Something went wrong
              </h1>
            }
            subTitle={
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            }
            extra={
              <Button
                type="primary"
                size="large"
                onClick={this.handleReset}
                className="mt-4"
                aria-label="Return to home page"
              >
                Return Home
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}
